import { useCallback, useContext, useEffect, useState } from "react";
import useAxiosRequests from "../../../hooks/useAxiosRequest";
import { Box, Checkbox, TextField, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ExternalRoutes from "../../../config/ExternalRoutes";
import { MessageContext } from "../../../context/MessageContext";


const RenderCellCalled = ({ row }) => {
    const { sendRequest } = useAxiosRequests();
    const { showSuccess, showError } = useContext(MessageContext)
    const [checked, setChecked] = useState(row.callRmbIsCalled);

    const updateData = async (newValue) => {
        try {
            const response = await sendRequest({
                method: "PUT",
                url: ExternalRoutes.callRemember.edit + row.id,
                body: JSON.stringify({ callRmbIsCalled: newValue })
            }
            );
            if (!response.status === 'error' || ![200, 201, 202, 204].includes(response.statusCode)) {
                showError(response.message);
            }
            showSuccess(response.message)
            setChecked(newValue);
        } catch (error) {
        }
    }

    return (
        <>
            <Tooltip title={row.value ? "Oui" : "Non"}>
                <Checkbox
                    checked={checked}
                    onChange={(e) => updateData(e.target.checked)}
                    inputProps={{ 'aria-label': 'controlled' }}
                />
            </Tooltip>
        </>
    )
};

const columns = [
    {
        field: "conUserId",
        headerName: "Utilisateur associé",
        flex: 0.15,
        sortable: true,
        minWidth: 120,
        valueGetter: (params) => {
            return params ? `${params.email}` : "";
        },
        renderCell: (params) => (
            <Tooltip title={params.row.conUserId ? `${params.row.conUserId.usrFirstname} ${params.row.conUserId.usrLastname}` : `${params.email}` }>
                <span>
                    {params.row.conUserId ? `${params.row.conUserId.usrFirstname} ${params.row.conUserId.usrLastname}` : `${params.email} `}
                </span>
            </Tooltip>
        ),
    },
    {
        field: "callRmbIsCalled",
        headerName: "Appelé",
        flex: 0.15,
        sortable: true,
        minWidth: 120,
        renderCell: RenderCellCalled
    },
    {
        field: "callRmbTel",
        headerName: "Telephone",
        flex: 0.15,
        sortable: true,
        minWidth: 120,
        renderCell: (params) => (
            <Tooltip title={params.value}>
                <span>{params.value || ""}</span>
            </Tooltip>
        ),
    },
    {
        field: "CallRmbDate",
        headerName: "Date",
        flex: 0.15,
        sortable: true,
        minWidth: 120,
        renderCell: (params) => {
            const date = params.value ? new Date(params.value) : null;
            const formattedDate = date
                ? `${date.toLocaleDateString('fr-FR')} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`
                : "";
            return (
                <Tooltip title={formattedDate}>
                    <span>{formattedDate}</span>
                </Tooltip>
            );
        },
    }
];


const CallRememberList = () => {
    const { sendRequest } = useAxiosRequests();
    const [loading, setLoading] = useState(true);
    const [callRemembers, setCallToRemembers] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortModel, setSortModel] = useState({});
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })



    const fetchData = useCallback(async () => {
        try {
            const response = await sendRequest({
                method: "GET",
                url: ExternalRoutes.callRemember.list,
                config: {
                    params: {
                        order: {
                            [sortModel.field]: sortModel.sort
                        },
                        page: paginationModel.page + 1,
                        itemsPerPage: paginationModel.pageSize,
                        query: searchQuery
                    },
                }
            })
            if (response) {
                setCallToRemembers(response);
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    }, [sendRequest, paginationModel.page, paginationModel.pageSize, searchQuery, sortModel.field, sortModel.sort]);

    useEffect(() => {
        fetchData();
    }, [sortModel, paginationModel, searchQuery]);

    const handleSortModelChange = (newSortingModel) => {
        setLoading(true);
        if (newSortingModel.length > 0) {
            setSortModel(newSortingModel[0])
        }
    };

    const handlePaginationChange = (newPaginationModel) => {
        setLoading(true);
        setPaginationModel(newPaginationModel)
    };

    return (
        <>
            <Box
                className="kl-admin-header"
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    🏢 Liste des se faire rappeler
                </Typography>
            </Box>

            <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="🔍 Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 2 }}
            />
            <DataGrid
                disableColumnMenu
                pagination
                rows={callRemembers.member}
                loading={loading}
                rowCount={callRemembers.totalItems !== undefined ? callRemembers.totalItems : 0}
                columns={columns}
                sortingMode="server"
                paginationMode="server"
                onSortModelChange={handleSortModelChange}
                autoHeight
                disableRowSelectionOnClick
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationChange}
                componentsProps={{
                    pagination: {
                        labelRowsPerPage: 'Lignes par page',
                        labelDisplayedRows: ({ from, to, count }) =>
                            `${from} Sur ${to} de ${count !== -1 ? count : `plus de ${to}`}`
                    }
                }}
                localeText={{
                    noRowsLabel: 'Aucune donnée disponible',
                }}
            />
        </>
    );
}

export default CallRememberList;